import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery } from 'react-fetching-library';
import useMetadataUpdate from '@shared/hooks/useMetadataUpdate';
import { getLocalizedDate } from '@shared/lib/localize';
import { formatPhoneForInfoPanel } from '@shared/lib/phone';
import { clearActivePatient, setActivePatientInfo, setActivePatientUuid } from 'redux_store';
import { fetchPatientAction, editPatientAction, createPatientAction } from '../api';

const normalizePatient = (payload) => {
  if (!payload) return null;
  return {
    ...payload,
    // Engagement master flag: missing (older API) means enabled, missing
    // reason means empty string. Normalizing HERE is load-bearing — this
    // object is the fast-json-patch diff baseline in useEditPatientForm,
    // and the API rejects an `/engagementStatusReason` patch op that
    // arrives without an `/engagementEnabled` flip. With the baseline
    // normalized, the diff emits engagement ops only on a genuine flip.
    engagementEnabled: payload.engagementEnabled !== false,
    engagementStatusReason: payload.engagementStatusReason ?? '',
    dateOfBirth: payload.dateOfBirth ? getLocalizedDate(payload.dateOfBirth, 'UTC') : '',
    phone: {
      ...payload.phone,
      phone: formatPhoneForInfoPanel(payload.phone?.phone || ''),
    },
    preferredPhone: {
      ...payload.preferredPhone,
      phone: formatPhoneForInfoPanel(payload.preferredPhone?.phone || ''),
    },
  };
};

/**
 * Fetches a patient by uuid and keeps redux's `activePatientSlice`
 * in sync so legacy listeners (e.g. `useOrderMetadata`) see the
 * same data as the new pages. Returns the normalized payload
 * (DOB localized, phone formatted) plus loading/error/reload.
 *
 * `applyServerPayload` lets a caller (typically the edit-form
 * orchestrator) push a just-saved patient body back into the
 * hook's state without a second GET. Without it, forms consuming
 * `patient` as a Formik `initialValues` source with
 * `enableReinitialize` reset back to the pre-save values on the
 * post-save render — Formik saw `props.initialValues` (still
 * stale) diverge from its just-reset internal state and undid the
 * change.
 */
export const usePatient = (accountUuid, patientUuid) => {
  const dispatch = useDispatch();
  const [patient, setPatient] = useState(null);
  const [error, setError] = useState(null);

  const { query, loading } = useMetadataUpdate(useQuery, [
    fetchPatientAction({ accountUuid, patientUuid }),
    false,
  ]);

  const reload = () => {
    if (!accountUuid || !patientUuid) return Promise.resolve();
    setError(null);
    return query().then(({ error: queryError, payload }) => {
      if (queryError) {
        setError(queryError);
        // Never leave the previous patient in place: the create-assessment
        // panels prefill email and phone from the active patient.
        dispatch(clearActivePatient());
        return null;
      }
      const normalized = normalizePatient(payload);
      setPatient(normalized);
      dispatch(setActivePatientUuid(patientUuid));
      dispatch(setActivePatientInfo(normalized));
      return normalized;
    });
  };

  const applyServerPayload = (payload) => {
    const normalized = normalizePatient(payload);
    setPatient(normalized);
    dispatch(setActivePatientInfo(normalized));
    return normalized;
  };

  useEffect(() => {
    reload();
    // reload is intentionally omitted from deps: it recreates on every
    // render, so including it would cause an infinite effect loop.
    // Re-runs are driven only by accountUuid / patientUuid changes.
  }, [accountUuid, patientUuid]);

  return { patient, loading, error, reload, applyServerPayload };
};

/**
 * Hook for patching a patient. Returns `{ mutate, loading }`:
 *
 *   - `mutate(patches)` PATCHes the patient with a JSON-patch array.
 *     Caller is responsible for computing the patch (e.g. via
 *     `fast-json-patch.compare`). On success, the promise resolves
 *     to `{ payload }`; on failure, to `{ error, status, payload }`.
 *   - `loading` mirrors the underlying `useMutation` state and is
 *     true while the PATCH is in flight.
 */
export const useEditPatient = (accountUuid, patientUuid) => {
  const action = (patches) => editPatientAction({ accountUuid, patientUuid, patches });
  const { mutate, loading } = useMetadataUpdate(useMutation, [action]);
  return { mutate, loading };
};

/**
 * Hook for creating a patient. Returns `{ mutate, loading }`:
 *
 *   - `mutate(patient)` PUTs the patient body. On success, the
 *     promise resolves to `{ payload }` where `payload` is the
 *     server-created patient (including `uuid`); on failure, to
 *     `{ error, status, payload }`.
 *   - `loading` mirrors the underlying `useMutation` state.
 *
 * The caller owns body shaping (DOB UTC, phones digits-only) — see
 * `@features/patient-form/useCreatePatientForm` for the canonical
 * pipeline.
 */
export const useCreatePatient = (accountUuid) => {
  const action = (patient) => createPatientAction({ accountUuid, patient });
  const { mutate, loading } = useMetadataUpdate(useMutation, [action]);
  return { mutate, loading };
};
